// QuickBooks handoff. One invoice, three shapes:
//
//   CSV  — QuickBooks ONLINE. Gear -> Import Data -> Invoices. This is the
//          format a shop can actually use today, no developer app required.
//   IIF  — QuickBooks DESKTOP / Enterprise. File -> Utilities -> Import ->
//          IIF Files. Tab-delimited, one TRNS + N SPL + ENDTRNS per invoice.
//   JSON — the QBO REST API Invoice object, for the future live sync. NOT
//          importable by hand — kept so the connector work has a head start.
//
// All three render from buildInvoiceLineItems() so the three files can never
// disagree on dollars. Same rule as invoiceDraft.js: the money math is
// EstimatePanel.calcTotals and nothing here re-derives it.

function toNumber(value, fallback = 0) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

function round2(n) {
  return Math.round((toNumber(n) + Number.EPSILON) * 100) / 100;
}

// Local calendar date, not UTC — a job invoiced at 7pm Eastern must not land
// on tomorrow's books.
function toDate(value = Date.now()) {
  const ms = value && value < 10_000_000_000 ? value * 1000 : value;
  const d = new Date(ms);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Takes a local "YYYY-MM-DD" and returns one N days later. Parsed part-by-part
// because `new Date('2025-07-25')` is UTC midnight, which reads back as the
// day before in any US timezone.
function addDays(isoDate, days) {
  const [y, m, d] = String(isoDate).split('-').map(Number);
  return new Date(y, m - 1, d + days);
}

// QuickBooks Desktop wants MM/DD/YYYY; the ISO date is built from a UTC
// slice, so split it rather than re-parsing into local time.
function toUsDate(isoDate) {
  const [y, m, d] = String(isoDate).split('-');
  return `${m}/${d}/${y}`;
}

function invoiceNumber(estimate, truck) {
  const ro = estimate?.roNumber || truck?.repairOrder || 'RO';
  const version = estimate?.version || 1;
  return `INV-${ro}-${version}`;
}

function unitInfo(truck = {}) {
  return [
    truck.truckNumber ? `Unit ${truck.truckNumber}` : '',
    [truck.year, truck.make, truck.model].filter(Boolean).join(' '),
    truck.vin ? `VIN ${truck.vin}` : '',
  ].filter(Boolean).join(' | ');
}

function accountingSettings(shopSettings = {}) {
  const a = shopSettings?.accounting || {};
  const items = a.items || {};
  const accounts = a.accounts || {};
  return {
    items: {
      labor:        items.labor        || 'Labor',
      parts:        items.parts        || 'Parts',
      shopSupplies: items.shopSupplies || 'Shop Supplies',
      tax:          items.tax          || 'Sales Tax',
    },
    accounts: {
      receivable:   accounts.receivable   || 'Accounts Receivable',
      labor:        accounts.labor        || 'Labor Income',
      parts:        accounts.parts        || 'Parts Income',
      shopSupplies: accounts.shopSupplies || 'Parts Income',
      tax:          accounts.tax          || 'Sales Tax Payable',
    },
  };
}

export function getQBOInvoiceNumber(estimate, truck) {
  return invoiceNumber(estimate, truck);
}

export function getQBODates(estimate, shopSettings = {}) {
  const paymentTermsDays = estimate?.paymentTermsDays
    ?? shopSettings?.pricing?.paymentTermsDays
    ?? shopSettings?.paymentTermsDays
    ?? 30;
  const invoiceDate = toDate(estimate?.invoicedAt || Date.now());
  return {
    invoiceDate,
    dueDate: toDate(addDays(invoiceDate, paymentTermsDays)),
    paymentTermsDays,
  };
}

// ─── The shared line list ─────────────────────────────────────────────────────

// Flattens every operation's labor + parts, then shop supplies and tax, into
// one normalized list. `kind` selects the QuickBooks item/account name;
// `taxable` reflects the operation's taxExempt flag so a state inspection
// doesn't get taxed downstream.
//
// Returns [{ kind, description, qty, rate, amount, taxable }]
export function buildInvoiceLineItems(estimate, truck) {
  const ops = estimate?.lines || [];
  const multiOp = ops.length > 1;

  const lines = ops.flatMap((op, i) => {
    const prefix = multiOp ? `[Op ${i + 1}${op.title ? ` ${op.title}` : ''}] ` : '';
    const taxable = !op.taxExempt;
    const opParts = (truck?.parts || []).filter(p => (op.partLineIds || []).includes(p.id));
    return [
      ...(op.laborLines || []).map(line => {
        const qty = toNumber(line.hours, 0) || 1;
        const amount = round2(line.amount);
        return {
          kind: 'labor',
          description: `${prefix}${line.description || 'Labor'}`,
          qty,
          rate: round2(toNumber(line.rate) || amount / (qty || 1)),
          amount,
          taxable,
        };
      }),
      ...opParts.map(part => {
        const qty = toNumber(part.quantity, 1) || 1;
        return {
          kind: 'parts',
          description: `${prefix}${part.description || 'Part'}${part.partNumber ? ` #${part.partNumber}` : ''}`,
          qty,
          rate: round2(part.price),
          amount: round2(toNumber(part.price) * qty),
          taxable,
        };
      }),
    ];
  });

  if (toNumber(estimate?.shopSupplies) > 0) {
    lines.push({
      kind: 'shopSupplies',
      description: 'Shop Supplies',
      qty: 1,
      rate: round2(estimate.shopSupplies),
      amount: round2(estimate.shopSupplies),
      taxable: true,
    });
  }

  if (toNumber(estimate?.tax) > 0) {
    lines.push({
      kind: 'tax',
      description: 'Sales Tax',
      qty: 1,
      rate: round2(estimate.tax),
      amount: round2(estimate.tax),
      taxable: false,
    });
  }

  return lines;
}

// Shared header block every format stamps on the document.
function invoiceHeader(estimate, truck, shopSettings) {
  const { invoiceDate, dueDate, paymentTermsDays } = getQBODates(estimate, shopSettings);
  return {
    docNumber: invoiceNumber(estimate, truck),
    customer: truck?.companyName || 'Customer',
    invoiceDate,
    dueDate,
    terms: `Net ${paymentTermsDays}`,
    // PO first in the memo: on a fleet or municipal invoice it's the field
    // accounts payable matches against, and an invoice without it gets
    // returned unpaid. QuickBooks Online has no dedicated PO column on the
    // invoice import, so the memo is where it has to live.
    memo: [
      truck?.poNumber ? `PO: ${truck.poNumber}` : null,
      `RO: ${estimate?.roNumber || truck?.repairOrder || ''}`,
      `Unit: ${unitInfo(truck)}`,
    ].filter(Boolean).join(' | '),
    message: estimate?.customerNotes || '',
    total: round2(estimate?.total),
  };
}

// ─── QuickBooks Online — CSV import ───────────────────────────────────────────

// Header text matches the sample file QuickBooks Online hands out under
// Settings -> Import data -> Invoices, so its column-mapping step auto-matches
// instead of making the shop map eleven columns by hand.
const CSV_COLUMNS = [
  'Invoice No.', 'Customer', 'Invoice Date', 'Due Date', 'Terms', 'Memo',
  'Item(Product/Service)', 'Item Description', 'Item Quantity', 'Item Rate',
  'Item Amount', 'Item Tax Code', 'Service Date',
];

// Intuit's documented ceilings for one import file.
export const QBO_MAX_INVOICES_PER_FILE = 100;
export const QBO_MAX_ROWS_PER_FILE = 1000;

function csvCell(value) {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// QuickBooks Online's import wizard asks which date format the file uses and
// offers the common ones; MM/DD/YYYY is the safe pick for a US shop.
function csvDate(isoDate) {
  return toUsDate(isoDate);
}

// Turns the shared line list into rows QuickBooks Online will actually accept.
// Two documented restrictions drive the work here:
//
//   1. The importer rejects negative charges, so REKON's discount lines (a
//      negative labor amount) can't ship as their own row. Each discount is
//      netted into the largest positive line and called out in that line's
//      description, which keeps the invoice total exactly right and visible.
//   2. With Automated Sales Tax, QuickBooks computes tax itself from the
//      per-line TAX/NON code. Sending our own "Sales Tax" row on top of that
//      double-taxes the invoice, so in the default mode we drop the tax row
//      and let QuickBooks do the math. Shops not on Automated Sales Tax can
//      switch to 'lineitem' in Settings -> Billing.
//
// Returns { rows, notes } — notes are plain-English warnings for the UI.
function qboCsvLines(estimate, truck, shopSettings) {
  const taxMode = shopSettings?.accounting?.qboTaxMode || 'quickbooks';
  const all = buildInvoiceLineItems(estimate, truck);
  const notes = [];

  const lines = all
    .filter(l => {
      if (l.kind !== 'tax') return true;
      if (taxMode === 'lineitem') return true;
      notes.push('Sales tax left to QuickBooks — lines are coded TAX/NON so Automated Sales Tax calculates it. Verify the tax QuickBooks lands on matches the invoice.');
      return false;
    })
    .map(l => ({ ...l }));

  const negatives = lines.filter(l => l.amount < 0);
  if (negatives.length) {
    const discount = negatives.reduce((s, l) => s + l.amount, 0); // negative
    const positives = lines.filter(l => l.amount > 0);
    const target = positives.sort((a, b) => b.amount - a.amount)[0];
    if (target) {
      const label = target.description;
      // Collapse to a single unit so quantity x rate equals the amount exactly
      // — a discounted hourly line otherwise rounds to a penny off, and
      // QuickBooks recomputes the amount from rate x quantity on import.
      target.amount = round2(target.amount + discount);
      target.qty = 1;
      target.rate = target.amount;
      target.description = `${label} (incl. ${fmtUsd(discount)} discount)`;
      notes.push(`QuickBooks Online won't import negative charges, so ${fmtUsd(discount)} of discount was netted into "${label}". The invoice total is unchanged.`);
    } else {
      notes.push(`This invoice is entirely negative (${fmtUsd(discount)}) — QuickBooks Online can't import it. Enter it in QuickBooks as a credit memo.`);
    }
    // Drop the now-absorbed negative rows.
    for (const n of negatives) lines.splice(lines.indexOf(n), 1);
  }

  const head = invoiceHeader(estimate, truck, shopSettings);
  const { items } = accountingSettings(shopSettings);
  const rows = lines.map(l => ([
    head.docNumber, head.customer, csvDate(head.invoiceDate), csvDate(head.dueDate),
    head.terms, head.memo, items[l.kind], l.description, l.qty,
    l.rate.toFixed(2), l.amount.toFixed(2),
    // Non-US files map these to the shop's own tax-code names in the wizard.
    l.taxable ? 'TAX' : 'NON',
    csvDate(head.invoiceDate),
  ]));

  return { rows, notes };
}

function fmtUsd(n) {
  return `$${Math.abs(toNumber(n)).toFixed(2)}`;
}

function csvText(rows) {
  return [CSV_COLUMNS, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

// One row per line item; the header fields repeat on every row, which is how
// QuickBooks Online groups rows into a single invoice on import.
export function buildQBOInvoiceCsv(estimate, truck, shopSettings = {}) {
  return csvText(qboCsvLines(estimate, truck, shopSettings).rows);
}

// Warnings the UI should show the shop before they hand the file to QuickBooks.
export function qboExportNotes(estimate, truck, shopSettings = {}) {
  return qboCsvLines(estimate, truck, shopSettings).notes;
}

// Many invoices in one file — Money's "export the whole queue" path.
// `entries` is [{ estimate, truck }].
export function buildQBOInvoiceCsvBatch(entries = [], shopSettings = {}) {
  const rows = entries.flatMap(({ estimate, truck }) => qboCsvLines(estimate, truck, shopSettings).rows);
  return csvText(rows);
}

// Splits a batch so no file exceeds Intuit's 100-invoice / 1,000-row ceilings.
// Returns [[{estimate, truck}, …], …] — one group per file.
export function chunkForQBO(entries = [], shopSettings = {}) {
  const groups = [];
  let current = [];
  let rowCount = 0;
  for (const entry of entries) {
    const n = qboCsvLines(entry.estimate, entry.truck, shopSettings).rows.length;
    const wouldOverflow = current.length >= QBO_MAX_INVOICES_PER_FILE
      || (rowCount + n > QBO_MAX_ROWS_PER_FILE && current.length > 0);
    if (wouldOverflow) {
      groups.push(current);
      current = [];
      rowCount = 0;
    }
    current.push(entry);
    rowCount += n;
  }
  if (current.length) groups.push(current);
  return groups;
}

// ─── QuickBooks Desktop — IIF import ──────────────────────────────────────────

const IIF_TRNS_COLS = ['!TRNS', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO', 'CLEAR', 'TOPRINT', 'DUEDATE', 'TERMS'];
const IIF_SPL_COLS  = ['!SPL',  'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO', 'CLEAR', 'QNTY', 'PRICE', 'INVITEM'];
const IIF_END_COLS  = ['!ENDTRNS'];

// Tabs are the delimiter, so they can never appear inside a field.
function iifCell(value) {
  return value == null ? '' : String(value).replace(/[\t\r\n]+/g, ' ');
}

function iifRow(cells) {
  return cells.map(iifCell).join('\t');
}

// One invoice = TRNS (debit A/R for the total) + one SPL per line (credit the
// income account, negative amount and negative quantity, which is how
// QuickBooks Desktop signs a sale) + ENDTRNS.
function iifTransaction(estimate, truck, shopSettings) {
  const head = invoiceHeader(estimate, truck, shopSettings);
  const { items, accounts } = accountingSettings(shopSettings);
  const lines = buildInvoiceLineItems(estimate, truck);
  const txnDate = toUsDate(head.invoiceDate);

  const out = [iifRow([
    'TRNS', 'INVOICE', txnDate, accounts.receivable, head.customer,
    round2(head.total).toFixed(2), head.docNumber, head.memo, 'N', 'Y',
    toUsDate(head.dueDate), head.terms,
  ])];

  for (const l of lines) {
    out.push(iifRow([
      'SPL', 'INVOICE', txnDate, accounts[l.kind], head.customer,
      (-l.amount).toFixed(2), head.docNumber, l.description, 'N',
      (-l.qty).toString(), l.rate.toFixed(2), items[l.kind],
    ]));
  }

  out.push('ENDTRNS');
  return out;
}

export function buildQBInvoiceIif(estimate, truck, shopSettings = {}) {
  return buildQBInvoiceIifBatch([{ estimate, truck }], shopSettings);
}

export function buildQBInvoiceIifBatch(entries = [], shopSettings = {}) {
  const body = entries.flatMap(({ estimate, truck }) => iifTransaction(estimate, truck, shopSettings));
  return [
    iifRow(IIF_TRNS_COLS),
    iifRow(IIF_SPL_COLS),
    iifRow(IIF_END_COLS),
    ...body,
  ].join('\r\n') + '\r\n';
}

// ─── QuickBooks Online — REST API Invoice object ──────────────────────────────

// Not hand-importable. Kept for the live-sync connector; renders from the same
// line list so it can never drift from the CSV/IIF dollar figures.
export function buildQBOInvoicePayload(estimate, truck, shopSettings = {}) {
  const head = invoiceHeader(estimate, truck, shopSettings);
  const { items } = accountingSettings(shopSettings);
  return {
    CustomerRef: { name: head.customer },
    DocNumber: head.docNumber,
    TxnDate: head.invoiceDate,
    DueDate: head.dueDate,
    PrivateNote: head.memo,
    CustomerMemo: { value: head.message },
    Line: buildInvoiceLineItems(estimate, truck).map(l => ({
      Description: l.description,
      Amount: l.amount,
      DetailType: 'SalesItemLineDetail',
      SalesItemLineDetail: {
        ItemRef: { name: items[l.kind] },
        Qty: l.qty,
        UnitPrice: l.rate,
        TaxCodeRef: { value: l.taxable ? 'TAX' : 'NON' },
      },
    })),
    TotalAmt: head.total,
  };
}

// ─── Format registry ──────────────────────────────────────────────────────────

// What the UI offers. `build` takes (estimate, truck, settings); `batch` takes
// ([{estimate, truck}], settings). Adding a format here adds it everywhere.
export const QB_FORMATS = {
  csv: {
    id: 'csv',
    label: 'QuickBooks Online (CSV)',
    hint: 'Gear → Import Data → Invoices',
    ext: 'csv',
    mime: 'text/csv;charset=utf-8',
    build: buildQBOInvoiceCsv,
    batch: buildQBOInvoiceCsvBatch,
  },
  iif: {
    id: 'iif',
    label: 'QuickBooks Desktop (IIF)',
    hint: 'File → Utilities → Import → IIF Files',
    ext: 'iif',
    mime: 'text/plain;charset=utf-8',
    build: buildQBInvoiceIif,
    batch: buildQBInvoiceIifBatch,
  },
  json: {
    id: 'json',
    label: 'QBO API payload (JSON)',
    hint: 'For a developer / live sync — not a QuickBooks import file',
    ext: 'json',
    mime: 'application/json',
    build: (est, truck, settings) => JSON.stringify(buildQBOInvoicePayload(est, truck, settings), null, 2),
    batch: (entries, settings) => JSON.stringify(
      entries.map(({ estimate, truck }) => buildQBOInvoicePayload(estimate, truck, settings)), null, 2),
  },
};

export const DEFAULT_QB_FORMAT = 'csv';

// Browser download of one invoice in the chosen format.
export function downloadQBInvoice(estimate, truck, shopSettings = {}, formatId = DEFAULT_QB_FORMAT) {
  const fmt = QB_FORMATS[formatId] || QB_FORMATS[DEFAULT_QB_FORMAT];
  const text = fmt.build(estimate, truck, shopSettings);
  const name = `${invoiceNumber(estimate, truck)}.${fmt.ext}`;
  triggerDownload(text, name, fmt.mime);
  return name;
}

// Browser download of many invoices. The QuickBooks Online CSV is split across
// as many files as Intuit's 100-invoice / 1,000-row ceilings require; the other
// formats have no such limit and come down as one file.
//
// Returns { files, invoices, notes } so the caller can tell the shop how many
// files to import and what to double-check.
export function downloadQBInvoiceBatch(entries = [], shopSettings = {}, formatId = DEFAULT_QB_FORMAT) {
  const fmt = QB_FORMATS[formatId] || QB_FORMATS[DEFAULT_QB_FORMAT];
  const stamp = toDate();
  const notes = [...new Set(entries.flatMap(({ estimate, truck }) =>
    (formatId === 'csv' ? qboExportNotes(estimate, truck, shopSettings) : [])))];

  const groups = formatId === 'csv' ? chunkForQBO(entries, shopSettings) : [entries];
  const files = groups.map((group, i) => {
    const suffix = groups.length > 1 ? `-${i + 1}of${groups.length}` : '';
    const name = `rekon-invoices-${stamp}${suffix}.${fmt.ext}`;
    triggerDownload(fmt.batch(group, shopSettings), name, fmt.mime);
    return name;
  });

  return { files, invoices: entries.length, notes };
}

function triggerDownload(text, filename, mime) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
